import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ListTree } from 'lucide-react'
import { seedRetail } from '../../api/maintenance'
import { StatusBadge } from '../../components/PageParts'
import { completeOnboardingStep, getOnboardingContext, nextAfter } from '../../utils/onboarding'

/** True while a new company is on the "set up the chart of accounts" step. */
export function useSeedingStep() {
  const [on] = useState(() => {
    const ctx = getOnboardingContext()
    return Boolean(ctx?.inOnboarding && ctx?.activeStep === 'utilities')
  })
  return on
}

/** Fills in the standard retail chart of accounts and the shared transaction accounts.
    Used after a reset, and as the first step for a new company. */
export function SeedChartCard() {
  const navigate = useNavigate()
  const isOnboarding = useSeedingStep()
  const [busy, setBusy] = useState(false)
  const [log, setLog] = useState([])

  const appendLog = (msg) => setLog((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`])

  const runSetup = async () => {
    setBusy(true); setLog([])
    try {
      appendLog('Setting up the retail chart of accounts…')
      const res = await seedRetail()
      const n = (x) => (Array.isArray(x) ? x.length : Object.keys(x || {}).length)
      appendLog(res ? `Added ${n(res.created)} accounts (${n(res.kept)} were already there) and set ${n(res.mapped)} transaction accounts.` : 'Done.')

      const onboarding = completeOnboardingStep('utilities')
      if (onboarding?.inOnboarding && onboarding?.nextRoute) {
        appendLog('Moving on to the next step…')
        // Navigate directly because the onboarding wizard is unmounted on this page.
        setTimeout(() => navigate(onboarding.nextRoute), 1200)
      } else if (isOnboarding) {
        // Keep the original fallback when the onboarding state is uncertain.
        appendLog('Moving on to Locations…')
        setTimeout(() => navigate('/master/locations'), 1500)
      }
    } catch (e) {
      appendLog(`Couldn’t finish: ${e.message || e}`)
    }
    setBusy(false)
  }

  return (
    <div className="ci-form-meta-card mf-form">
      <div className="mf-card-title set-card-title">
        <ListTree size={16} />Chart of accounts
        {isOnboarding && <StatusBadge label="Getting started" tone="info" />}
      </div>
      <div className="set-body">
        <p>Adds any of the standard retail accounts, and the transaction accounts that sales, purchases and stock post to, that your company doesn’t have yet. Accounts and choices you already have are left as they are, so it’s safe to run again. Use it after a reset.</p>
        {isOnboarding && <p className="doc-hint">This is the first step of setting up your company. Afterwards you’ll go on to {nextAfter('utilities').step?.title || 'the next step'}.</p>}
        <div className="set-actions">
          <button type="button" className="ci-btn ci-btn-primary" disabled={busy} onClick={runSetup}>{busy ? 'Setting up…' : 'Set up the chart of accounts'}</button>
        </div>
        {log.length > 0 && <pre className="set-log">{log.join('\n')}</pre>}
      </div>
    </div>
  )
}
